import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import _ from 'lodash';
import {apiPost} from '../api';
import {addRequestCases, requestDefaultState} from './reducerUtils';
import {folderFilterOptions, folderSortOptions, pinFilterOptions, pinSortOptions} from 'common';


const initialSearchState = {
  ...requestDefaultState,
  filter: null,
  filterOptions: [
    {emoji: '🔥', label: '🔥 Hot', value: pinFilterOptions.hot},
    {emoji: '♾️', label: '♾️ All', value: pinFilterOptions.all},
    {emoji: '😎', label: '😎 My Places', value: pinFilterOptions.createdByUser},
    {emoji: '💖', label: '💖 My Favorites', value: pinFilterOptions.likedByUser},
    {emoji: '⛰️', label: '⛰️ 3d Places', value: pinFilterOptions.only3d},
  ],
  defaultSort: pinSortOptions.likes,
  sort: pinSortOptions.likes,
  sortOptions: [
    // {label: 'Recommended', value: sortOptions.recommended},
    {label: '💞 Most Liked', value: pinSortOptions.likes},
    {label: '🐣 Newest', value: pinSortOptions.creationDateDesc},
    {label: '🐓 Oldest', value: pinSortOptions.creationDateAsc},
    {label: '🔼 A-Z', value: pinSortOptions.labelAsc},
    {label: '🔽 Z-A', value: pinSortOptions.labelDesc},
  ],
};


const initialState = {
  clusters: requestDefaultState,
  clustersInApp: requestDefaultState,
  earthDayClue: requestDefaultState,
  earthDayItemClaim: requestDefaultState,
  earthDayItemsClaimed: requestDefaultState,
  earthDayItemsInApp: requestDefaultState,
  earthDayItemsInBounds: requestDefaultState,
  folderDelete: requestDefaultState,
  folderEditForm: {},
  folderEditFormSave: requestDefaultState,
  folderOpened: undefined, // folder document
  foldersListFeatured: requestDefaultState,
  foldersListSearch: {
    ...initialSearchState,
    filterOptions: [
      {emoji: '♾️', label: '♾️ All', value: folderFilterOptions.all},
      {emoji: '😎', label: '😎 My Lists', value: folderFilterOptions.createdByUser},
    ],
    defaultSort: folderSortOptions.recommended,
    sort: folderSortOptions.recommended,
    sortOptions: [
      {label: '🦉 Recommended', value: folderSortOptions.recommended},
      {label: '🐣 Newest', value: folderSortOptions.creationDateDesc},
      {label: '🐓 Oldest', value: folderSortOptions.creationDateAsc},
      {label: '🔼 A-Z', value: folderSortOptions.labelAsc},
      {label: '🔽 Z-A', value: folderSortOptions.labelDesc},
    ],
  },
  foldersListSharedInWorld: requestDefaultState,
  foldersListUser: requestDefaultState,
  isNewDataAvailable: false,
  photosUpload: requestDefaultState,
  pinDelete: requestDefaultState,
  pinEditForm: {},
  pinEditFormSave: requestDefaultState,
  pinRating: requestDefaultState,
  pinRemoveFromFeaturedRequest: requestDefaultState,
  pinSaveRequest: requestDefaultState,
  pinUnsavedTemp: {}, // same as edit form for showing unsaved in list view
  pinsBalanceRequest: requestDefaultState,
  pinsByOwner: {
    ownerAlias: undefined, // to show avatar when ownerId has a value
    ownerId: undefined,
    ownerProfilePhotoURL: undefined,
  },
  pinsListByOwner: initialSearchState,
  pinsListFolder: initialSearchState,
  pinsListSearch: initialSearchState,
  pinsPanoIdSearch: initialSearchState,
};

export const clustersRequest = createAsyncThunk(
  'pins/clusters',
  async ({bounds, worldId, zoom}, thunkAPI) => {
    const {data} = await apiPost(`/pins/clusters`, {bounds, worldId, zoom});
    return data;
  },
);

export const earthDayClueRequest = createAsyncThunk(
  'pins/earthDayClue',
  async ({}, thunkAPI) => {
    const {data} = await apiPost(`/games/earthDay/clue`, {});
    return data;
  },
);

export const earthDayItemClaimRequest = createAsyncThunk(
  'pins/earthDayItemClaim',
  async ({itemId}, thunkAPI) => {
    const {data} = await apiPost(`/games/earthDay/itemClaim`, {itemId});
    return data;
  },
);

export const earthDayItemsClaimedRequest = createAsyncThunk(
  'pins/earthDayItemsClaimed',
  async ({}, thunkAPI) => {
    const {data} = await apiPost(`/games/earthDay/itemsClaimed`, {});
    return data;
  },
);

export const earthDayItemsInAppRequest = createAsyncThunk(
  'pins/earthDayItemsInApp',
  async ({bounds, worldId, zoom}, thunkAPI) => {
    const {data} = await apiPost(`/games/earthDay/itemsInBounds`, {bounds, zoom});
    return data;
  },
);

export const earthDayItemsInBoundsRequest = createAsyncThunk(
  'pins/earthDayItemsInBounds',
  async ({bounds, worldId, zoom}, thunkAPI) => {
    const {data} = await apiPost(`/games/earthDay/itemsInBounds`, {bounds, zoom});
    return data;
  },
);

export const clustersInAppRequest = createAsyncThunk(
  'pins/clustersInApp',
  async ({bounds, worldId, zoom}, thunkAPI) => {
    const {data} = await apiPost(`/pins/clusters`, {bounds, worldId, zoom});
    return data;
  },
);

export const folderEditFormSaveRequest = createAsyncThunk(
  'pins/folderEditFormSaveRequest',
  async (payload, {getState}) => {
    const {pins: {folderEditForm}} = getState();
    const {data} = await apiPost(`/folders/createOrUpdate`, folderEditForm);
    return data;
  },
);

export const photosUpload = createAsyncThunk(
  'pins/photosUpload',
  async ({files}, thunkAPI) => {
    const formData = new FormData();
    _.forEach(files, file => formData.append('photos', file));
    const {data} = await apiPost(`/pins/photos/upload`, formData);
    return data;
  },
);

export const photosView = createAsyncThunk(
  'pins/photosView',
  async ({photosS3Keys}, thunkAPI) => {
    const {data} = await apiPost(`/pins/photos/view`, {photosS3Keys});
    return data;
  },
);

export const pinsBalanceRequest = createAsyncThunk(
  'pins/pinsBalanceRequest',
  async ({}, thunkAPI) => {
    const {data} = await apiPost(`/pins/balance`, {});
    return data;
  },
);

export const pinEditFormSaveRequest = createAsyncThunk(
  'pins/pinEditFormSaveRequest',
  async (payload, {dispatch, getState}) => {
    const {inApp: {world: {_id: worldId}}, pins: {folderEditForm}} = getState();

    if (!_.isEmpty(folderEditForm)) {
      const {error, payload} = await dispatch(folderEditFormSaveRequest());
      if (error) throw error;
      dispatch(pinEditFormUpdate({folderId: payload._id}));
    }

    // for browser-only testing
    // dispatch(pinEditFormUpdate({
    //   location: {
    //     Latitude: 0, Longitude: 0
    //   }
    // }));

    const {pins: {pinEditForm}} = getState();
    const {data} = await apiPost(`/pins/createOrUpdate`, {
      ...pinEditForm,
      worldId,
    });
    return data;
  },
);

export const pinSaveRequest = createAsyncThunk(
  'pins/pinSaveRequest',
  async (payload, {dispatch, getState}) => {
    const {inApp: {world: {_id: worldId}}} = getState();

    const {
      address,
      altitude,
      folderId,
      label,
      panoId,
      panoIdIsApi2025,
      panoLocation: {latitude, longitude},
      suggestedLabel,
      zoomLevel,
    } = payload;

    const {data} = await apiPost(`/pins/createOrUpdate`, {
      address,
      altitude,
      folderId,
      label,
      location: {Latitude: latitude, Longitude: longitude},
      streetViewId: panoId,
      streetViewIdIsApi2025: panoIdIsApi2025,
      suggestedLabel,
      worldId,
      zoomLevel,
    });
    return data;
  },
);

export const pinsSearchRequest = createAsyncThunk(
  'pins/pinsSearchRequest',
  async ({isNew, searchString = '', panoId}, {getState}) => {
    const {inApp: {world: {_id: worldId}}, pins: {pinsListSearch}} = getState();
    const {ids, filter, folderOpened, sort} = pinsListSearch;
    const {data} = await apiPost(`/pins/search`, {
      filter: panoId ? pinFilterOptions.all : filter,
      ids,
      limit: 18,
      page: isNew ? 1 : _.get(pinsListSearch, 'response.nextPage', 1),
      panoId,
      searchString,
      sort: panoId ? 'owner' : sort,
      worldId,
    });
    return data;
  },
);

export const pinsByOwnerSearchRequest = createAsyncThunk(
  'pins/pinsByOwnerSearchRequest',
  async ({isNew, searchString = '', panoId}, {getState}) => {
    const {inApp: {world: {_id: worldId}}, pins: {pinsByOwner, pinsListByOwner}} = getState();
    const {ids, filter, folderOpened, ownerAlias, ownerId, sort} = pinsListByOwner;
    const {data} = await apiPost(`/pins/search`, {
      filter: panoId ? pinFilterOptions.all : filter,
      ids,
      limit: 18,
      ownerId: pinsByOwner.ownerId,
      page: isNew ? 1 : _.get(pinsListByOwner, 'response.nextPage', 1),
      panoId,
      searchString,
      sort: panoId ? 'owner' : sort,
      worldId,
    });
    return data;
  },
);

export const pinsPanoIdSearchRequest = createAsyncThunk(
  'pins/pinsPanoIdSearchRequest',
  async ({isNew, panoId}, {getState}) => {
    const {inApp: {world: {_id: worldId}}, pins: {pinsPanoIdSearch}} = getState();
    const {filter, folderOpened, sort} = pinsPanoIdSearch;
    const {data} = await apiPost(`/pins/search`, {
      filter: panoId ? pinFilterOptions.all : filter,
      limit: 100,
      page: isNew ? 1 : _.get(pinsPanoIdSearch, 'response.nextPage', 1),
      panoId,
      sort: 'owner',
      worldId,
    });
    return data;
  },
);

export const foldersSearchRequest = createAsyncThunk(
  'pins/foldersSearchRequest',
  async ({isNew}, {getState}) => {
    const {
      inApp: {world: {_id: worldId}},
      pins: {foldersListSearch},
      ui: {txtInputPlacesLists: {textInputField}}
    } = getState();
    const {filter, sort} = foldersListSearch;
    const {data} = await apiPost(`/folders/search`, {
      filter,
      limit: 18,
      page: isNew ? 1 : _.get(foldersListSearch, 'response.nextPage', 1),
      searchString: textInputField,
      sort,
      worldId,
    });
    return data;
  },
);


export const folderDeleteRequest = createAsyncThunk(
  'pins/folderDeleteRequest',
  async (payload, {getState}) => {
    const {
      folderId,
      deletePins,
    } = payload;

    const {data} = await apiPost(`/folders/delete`, {
      folderId,
      deletePins,
    });
    return data;
  },
);

export const pinDeleteRequest = createAsyncThunk(
  'pins/pinDeleteRequest',
  async (payload, {getState}) => {
    const {
      pinId,
    } = payload;

    const {data} = await apiPost(`/pins/delete`, {
      pinId,
    });
    return data;
  },
);

export const pinRemoveFromFeaturedRequest = createAsyncThunk(
  'pins/pinRemoveFromFeaturedRequest',
  async (payload, {getState}) => {
    const {
      pinId,
    } = payload;

    const {pins: {folderOpened: {_id: folderId}}} = getState();

    const {data} = await apiPost(`/folders/folderFeatured/pinRemove`, {
      folderId,
      pinId,
    });
    return data;
  },
);

export const pinRatingCreateRequest = createAsyncThunk(
  'pins/pinRatingCreateRequest',
  async (payload, {getState}) => {
    const {
      pinId,
      vote,
    } = payload;

    const {data} = await apiPost(`/ratings/create`, {
      contentId: pinId,
      vote,
    });
    return data;
  },
);
export const pinRatingDeleteRequest = createAsyncThunk(
  'pins/pinRatingDeleteRequest',
  async (payload, {getState}) => {
    const {
      pinId,
    } = payload;

    const {data} = await apiPost(`/ratings/delete`, {
      contentId: pinId,
    });
    return data;
  },
);


export const pinsFolderRequest = createAsyncThunk(
  'pins/pinsFolderRequest',
  async ({isNew, searchString = ''}, {getState}) => {
    const {inApp: {world: {_id: worldId}}, pins: {folderOpened, pinsListFolder}} = getState();

    const featuredIds = _.get(folderOpened, 'featuredIds');

    const {data} = await apiPost(`/pins/search`, {
      folderId: !_.isEmpty(featuredIds) ? null : _.get(folderOpened, '_id'),
      ids: _.isEmpty(featuredIds) ? null : featuredIds,
      limit: 18,
      page: isNew ? 1 : _.get(pinsListFolder, 'response.nextPage', 1),
      searchString,
      sort: pinsListFolder.sort,
      worldId,
    });
    return data;
  },
);

export const foldersFeaturedRequest = createAsyncThunk(
  'pins/foldersFeaturedRequest',
  async ({
    worldId,
  }) => {
    const {data} = await apiPost(`/folders/foldersListFeatured`, {});
    return data;
  },
);

export const foldersListSharedInWorldRequest = createAsyncThunk(
  'pins/foldersListSharedInWorldRequest',
  async ({
    worldId,
  }) => {
    const {data} = await apiPost(`/folders/foldersListSharedInWorld`, {worldId});
    return data;
  },
);

export const foldersListUserRequest = createAsyncThunk(
  'pins/foldersListUserRequest',
  async ({
    worldId,
  }) => {
    const {data} = await apiPost(`/folders/foldersListUser`, {});
    return data;
  },
);

export const pinsSlice = createSlice({
  name: 'places',
  initialState,
  reducers: {
    clustersInAppClear: (state, {}) => {
      state.clustersInApp = requestDefaultState;
    },
    earthDayItemsInAppClear: (state, {}) => {
      state.earthDayItemsInApp = requestDefaultState;
    },
    folderEditFormSet: (state, {payload}) => {
      state.folderEditForm = payload;
    },
    folderEditFormUpdate: (state, {payload}) => {
      state.folderEditForm = {
        ...state.folderEditForm,
        ...payload,
      };
    },
    folderClose: (state, {payload}) => {
      state.folderOpened = undefined;
    },
    folderOpen: (state, {payload}) => {
      state.folderOpened = payload;
      state.pinsListFolder.response = null;
      const sort = payload.isOwnedByUser ? pinSortOptions.creationDateDesc : pinSortOptions.likes;
      state.pinsListFolder.defaultSort = sort;
      state.pinsListFolder.sort = sort;
    },
    foldersSearchFilterSet: (state, {payload}) => {
      state.foldersListSearch.filter = payload;
      state.foldersListSearch.response = null;
    },
    foldersSearchSortSet: (state, {payload}) => {
      state.foldersListSearch.sort = payload;
      state.foldersListSearch.response = null;
    },
    foldersSearchResultsClear: (state, {}) => {
      state.foldersListSearch.isLoading = false;
      state.foldersListSearch.response = null;
    },
    isNewDataAvailableSet: (state, {payload}) => {
      state.isNewDataAvailable = payload;
    },
    pinEditFormSet: (state, {payload}) => {
      state.pinEditForm = payload;
    },
    pinUnsavedTempSet: (state, {payload}) => {
      state.pinUnsavedTemp = payload;
    },
    pinEditFormUpdate: (state, {payload}) => {
      state.pinEditForm = {
        ...state.pinEditForm,
        ...payload,
      };
    },
    pinsListByOwnerResultsClear: (state, {}) => {
      state.pinsListByOwner.isLoading = false;
      state.pinsListByOwner.response = null;
    },
    pinsListByOwnerSortSet: (state, {payload}) => {
      state.pinsListByOwner.sort = payload;
      state.pinsListByOwner.response = null;
    },
    pinsListFolderResultsClear: (state, {}) => {
      state.pinsListFolder.isLoading = false;
      state.pinsListFolder.response = null;
    },
    pinsListFolderSortSet: (state, {payload}) => {
      state.pinsListFolder.sort = payload;
      state.pinsListFolder.response = null;
    },
    pinsSearchFilterSet: (state, {payload}) => {
      state.pinsListSearch.filter = payload;
      state.pinsListSearch.response = null;
    },
    pinsSearchIdsSet: (state, {payload}) => {
      state.pinsListSearch.ids = payload;
      state.pinsListSearch.response = null;
    },
    pinsByOwnerClear: (state, {}) => {
      state.pinsByOwner = {
        ownerAlias: undefined,
        ownerId: undefined,
        ownerProfilePhotoURL: undefined,
      }
    },
    pinsByOwnerSet: (state, {payload: {
      ownerAlias,
      ownerId,
      ownerProfilePhotoURL,
    }}) => {
      state.pinsByOwner = {
        ownerAlias,
        ownerId,
        ownerProfilePhotoURL,
      }
    },
    pinsSearchResultsClear: (state, {}) => {
      state.pinsListSearch.isLoading = false;
      state.pinsListSearch.response = null;
    },
    pinsPanoIdSearchResultsClear: (state, {}) => {
      state.pinsPanoIdSearch.isLoading = false;
      state.pinsPanoIdSearch.response = null;
    },
    pinsSearchSortSet: (state, {payload}) => {
      state.pinsListSearch.sort = payload;
      state.pinsListSearch.response = null;
    },
  },
  extraReducers: (builder) => { // https://redux-toolkit.js.org/api/createAsyncThunk#examples
    addRequestCases(builder, earthDayClueRequest, 'earthDayClue');
    addRequestCases(builder, earthDayItemClaimRequest, 'earthDayItemClaim');
    addRequestCases(builder, earthDayItemsClaimedRequest, 'earthDayItemsClaimed');
    addRequestCases(builder, earthDayItemsInBoundsRequest, 'earthDayItemsInBounds');
    addRequestCases(builder, earthDayItemsInAppRequest, 'earthDayItemsInApp');

    addRequestCases(builder, clustersRequest, 'clusters');
    addRequestCases(builder, clustersInAppRequest, 'clustersInApp');

    addRequestCases(builder, folderEditFormSaveRequest, 'folderEditFormSave', {
      fulfilledReducer: (state, action) => {
        const {folderEditFormSave, folderOpened, foldersListSearch, foldersListUser} = state;
        folderEditFormSave.isLoading = false;
        folderEditFormSave.response = action.payload;

        const updatedFolder = action.payload;

        if (foldersListUser.response) { // update existing folder in foldersListUser or add it to the list if it's new
          const i = _.findIndex(foldersListUser.response, f => f._id === updatedFolder._id);
          if (i >= 0) foldersListUser.response[i] = updatedFolder;
          else foldersListUser.response.unshift(updatedFolder);
        }

        if (foldersListSearch.response) { // update existing folder in foldersListSearch or add it to the list if it's new
          const i = _.findIndex(foldersListSearch.response.folders, f => f._id === updatedFolder._id);
          if (i >= 0) foldersListSearch.response.folders[i] = updatedFolder;
          else foldersListSearch.response.folders.unshift(updatedFolder);
        }

        // if folder drawer is open, update it
        if (folderOpened && folderOpened._id === updatedFolder._id)
          state.folderOpened = updatedFolder;
      }
    });

    addRequestCases(builder, foldersSearchRequest, 'foldersListSearch', {
      fulfilledReducer: (state, action) => {
        const {foldersListSearch} = state;
        foldersListSearch.isLoading = false;

        // response is return val from paginateAggregate with `docs` aliased as `pins`
        // https://github.com/aravindnc/mongoose-aggregate-paginate-v2#modelaggregatepaginateaggregatequery-options-callback
        foldersListSearch.response = {
          ...action.payload,
          folders: [
            ...action.payload.page === 1 ? [] : _.get(foldersListSearch, 'response.folders', []),
            ...action.payload.folders,
          ],
        };
      }
    });

    addRequestCases(builder, pinsBalanceRequest, 'pinsBalanceRequest');
    addRequestCases(builder, pinSaveRequest, 'pinSaveRequest');

    addRequestCases(builder, pinsByOwnerSearchRequest, 'pinsListByOwner', {
      fulfilledReducer: (state, action) => {
        const {pinsListByOwner} = state;
        pinsListByOwner.isLoading = false;

        // response is return val from paginateAggregate with `docs` aliased as `pins`
        // https://github.com/aravindnc/mongoose-aggregate-paginate-v2#modelaggregatepaginateaggregatequery-options-callback
        pinsListByOwner.response = {
          ...action.payload,
          pins: [
            ...action.payload.page === 1 ? [] : _.get(pinsListByOwner, 'response.pins', []),
            ...action.payload.pins,
          ],
        };
      }
    });

    addRequestCases(builder, pinsFolderRequest, 'pinsListFolder', {
      fulfilledReducer: (state, action) => {
        const {pinsListFolder} = state;
        pinsListFolder.isLoading = false;

        // response is return val from paginateAggregate with `docs` aliased as `pins`
        // https://github.com/aravindnc/mongoose-aggregate-paginate-v2#modelaggregatepaginateaggregatequery-options-callback
        pinsListFolder.response = {
          ...action.payload,
          pins: [
            ...action.payload.page === 1 ? [] : _.get(pinsListFolder, 'response.pins', []),
            ...action.payload.pins,
          ],
        };
      }
    });
    addRequestCases(builder, pinEditFormSaveRequest, 'pinEditFormSave', {
      fulfilledReducer: (state, action) => {
        const {
          folderOpened,
          pinEditFormSave,
          pinsBalanceRequest,
          pinsListFolder,
          pinsListSearch,
          pinsPanoIdSearch,
        } = state;

        pinEditFormSave.isLoading = false;
        const {pin: updatedPin, pinsBalance} = action.payload;
        pinEditFormSave.response = updatedPin;
        pinsBalanceRequest.response = {pinsBalance};

        // update if in search results
        if (pinsListSearch.response) {
          const i = _.findIndex(pinsListSearch.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) pinsListSearch.response.pins[i] = updatedPin;
        }

        // update if in search results
        if (pinsPanoIdSearch.response) {
          const i = _.findIndex(pinsPanoIdSearch.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) pinsPanoIdSearch.response.pins[i] = updatedPin;
        }

        // update if still in folder, remove if moved to different folder
        if (folderOpened && pinsListFolder.response.pins) {
          const i = _.findIndex(pinsListFolder.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            if (folderOpened._id === updatedPin.folderId)
              pinsListFolder.response.pins[i] = updatedPin;
            else pinsListFolder.response.pins.splice(i, 1);
          }
        }
      }
    });
    addRequestCases(builder, pinRatingCreateRequest, 'pinRating', {
      fulfilledReducer: (state, action) => {
        const {pinRating, pinsListFolder, pinsPanoIdSearch, pinsListSearch} = state;
        pinRating.isLoading = false;
        pinRating.response = action.payload;

        const updatedPin = action.payload;

        const updateExistingPin = (existing) => ({
          ...existing,
          ..._.pick(updatedPin, ['dislikedBy', 'dislikes', 'likedBy', 'likes', 'isLikedByUser', 'isDislikedByUser']),
        })

        // update if in search results
        if (_.get(pinsListSearch, 'response.pins')) {
          const i = _.findIndex(pinsListSearch.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            pinsListSearch.response.pins[i] = updateExistingPin(pinsListSearch.response.pins[i]);
          }
        }

        // update if in search results
        if (_.get(pinsPanoIdSearch, 'response.pins')) {
          const i = _.findIndex(pinsPanoIdSearch.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            pinsPanoIdSearch.response.pins[i] = updateExistingPin(pinsPanoIdSearch.response.pins[i]);
          }
        }

        // update if still in folder
        if (_.get(pinsListFolder, 'response.pins')) {
          const i = _.findIndex(pinsListFolder.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            pinsListFolder.response.pins[i] = updateExistingPin(pinsListFolder.response.pins[i]);
          }
        }
      }
    });
    addRequestCases(builder, pinRatingDeleteRequest, 'pinRating', {
      fulfilledReducer: (state, action) => {
        const {pinRating, pinsListFolder, pinsPanoIdSearch, pinsListSearch} = state;
        pinRating.isLoading = false;
        pinRating.response = action.payload;

        const updatedPin = action.payload;
        const updateExistingPin = (existing) => ({
          ...existing,
          ..._.pick(updatedPin, ['dislikedBy', 'dislikes', 'likedBy', 'likes', 'isLikedByUser', 'isDislikedByUser']),
        })

        // update if in search results
        if (_.get(pinsListSearch, 'response.pins')) {
          const i = _.findIndex(pinsListSearch.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            pinsListSearch.response.pins[i] = updateExistingPin(pinsListSearch.response.pins[i]);
          }
        }

        // update if in search results
        if (_.get(pinsPanoIdSearch, 'response.pins')) {
          const i = _.findIndex(pinsPanoIdSearch.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            pinsPanoIdSearch.response.pins[i] = updateExistingPin(pinsPanoIdSearch.response.pins[i]);
          }
        }

        // update if still in folder
        if (_.get(pinsListFolder, 'response.pins')) {
          const i = _.findIndex(pinsListFolder.response.pins, p => p._id === updatedPin._id);
          if (i >= 0) {
            pinsListFolder.response.pins[i] = updateExistingPin(pinsListFolder.response.pins[i]);
          }
        }
      }
    });
    addRequestCases(builder, pinsPanoIdSearchRequest, 'pinsPanoIdSearch', {
      fulfilledReducer: (state, action) => {
        const {pinsPanoIdSearch} = state;
        pinsPanoIdSearch.isLoading = false;

        // response is return val from paginateAggregate with `docs` aliased as `pins`
        // https://github.com/aravindnc/mongoose-aggregate-paginate-v2#modelaggregatepaginateaggregatequery-options-callback
        pinsPanoIdSearch.response = {
          ...action.payload,
          pins: [
            ...action.payload.page === 1 ? [] : _.get(pinsPanoIdSearch, 'response.pins', []),
            ...action.payload.pins,
          ],
        };
      }
    });
    addRequestCases(builder, pinsSearchRequest, 'pinsListSearch', {
      fulfilledReducer: (state, action) => {
        const {pinsListSearch} = state;
        pinsListSearch.isLoading = false;

        // response is return val from paginateAggregate with `docs` aliased as `pins`
        // https://github.com/aravindnc/mongoose-aggregate-paginate-v2#modelaggregatepaginateaggregatequery-options-callback
        pinsListSearch.response = {
          ...action.payload,
          pins: [
            ...action.payload.page === 1 ? [] : _.get(pinsListSearch, 'response.pins', []),
            ...action.payload.pins,
          ],
        };
      }
    });
    addRequestCases(builder, folderDeleteRequest, 'folderDelete', {
      fulfilledReducer: (state, action) => {
        const {folderDelete, foldersListSearch, foldersListUser, folderOpened} = state;
        folderDelete.isLoading = false;
        const {folderId, deletePins} = action.meta.arg;

        {
          // update if in foldersListUser
          if (foldersListUser.response) {
            const i = _.findIndex(foldersListUser.response, f => f._id === folderId);
            if (i >= 0) foldersListUser.response.splice(i, 1);
          }
        }

        if (foldersListSearch.response) { // update if in foldersListSearch
          const i = _.findIndex(foldersListSearch.response.folders, f => f._id === folderId);
          if (i >= 0) foldersListSearch.response.folders.splice(i, 1);
        }

        if (folderOpened) {
          state.folderOpened = undefined;
          if (deletePins) {
            state.pinsListFolder.response = null;
            state.pinsListSearch.response = null;
          }
        }
      }
    });
    addRequestCases(builder, pinDeleteRequest, 'pinDelete', {
      fulfilledReducer: (state, action) => {
        const {pinDelete, pinsListFolder, pinsPanoIdSearch, pinsListSearch} = state;
        pinDelete.isLoading = false;
        const {pinId} = action.meta.arg;

        // update if in search results
        if (_.get(pinsListSearch.response, 'pins')) {
          const i = _.findIndex(pinsListSearch.response.pins, p => p._id === pinId);
          if (i >= 0) pinsListSearch.response.pins.splice(i, 1);
        }

        // update if in search results
        if (_.get(pinsPanoIdSearch.response, 'pins')) {
          const i = _.findIndex(pinsPanoIdSearch.response.pins, p => p._id === pinId);
          if (i >= 0) pinsPanoIdSearch.response.pins.splice(i, 1);
        }

        // update if in folder results
        if (_.get(pinsListFolder.response, 'pins')) {
          const i = _.findIndex(pinsListFolder.response.pins, p => p._id === pinId);
          if (i >= 0) pinsListFolder.response.pins.splice(i, 1);
        }
      }
    });
    addRequestCases(builder, pinRemoveFromFeaturedRequest, 'pinRemoveFromFeaturedRequest', {
      fulfilledReducer: (state, action) => {
        const {pinRemoveFromFeaturedRequest, pinsListFolder} = state;
        pinRemoveFromFeaturedRequest.isLoading = false;
        const {pinId} = action.meta.arg;

        // update if in folder results
        if (_.get(pinsListFolder.response, 'pins')) {
          const i = _.findIndex(pinsListFolder.response.pins, p => p._id === pinId);
          if (i >= 0) pinsListFolder.response.pins.splice(i, 1);
        }
      }
    });
    // addRequestCases(builder, pinRemoveFromFeaturedRequest, 'pinRemoveFromFeaturedRequest', {})
    addRequestCases(builder, foldersFeaturedRequest, 'foldersListFeatured');
    addRequestCases(builder, foldersListSharedInWorldRequest, 'foldersListSharedInWorld');
    addRequestCases(builder, foldersListUserRequest, 'foldersListUser');
  }
});

export const {
  clustersInAppClear,
  earthDayItemsInAppClear,
  folderClose,
  folderEditFormSet,
  folderEditFormUpdate,
  folderOpen,
  foldersSearchFilterSet,
  foldersSearchResultsClear,
  foldersSearchSortSet,
  isNewDataAvailableSet,
  pinEditFormSet,
  pinEditFormUpdate,
  pinUnsavedTempSet,
  pinsByOwnerClear,
  pinsByOwnerSet,
  pinsListByOwnerResultsClear,
  pinsListByOwnerSortSet,
  pinsListFolderResultsClear,
  pinsListFolderSortSet,
  pinsPanoIdSearchResultsClear,
  pinsSearchFilterSet,
  pinsSearchIdsSet,
  pinsSearchResultsClear,
  pinsSearchSortSet,
} = pinsSlice.actions;

export default pinsSlice.reducer;
